import { useEffect, useState } from "react";
import type { ModelParam, ModelValues } from "../api/types";
import { Slider } from "../ui";
import { humanise } from "../viewer/math";
import { WAIT_TITLE } from "./HistoryPanel";
import { paramLabel, shownUnit } from "./format";

/** Metres from the centimetres a slider holds, without the float's dust. */
const metres = (cmValue: number) => Math.round(cmValue * 1e4) / 1e6;

/** "Tune": one control for each setting the design's code declares
 *  ([design-studio §11.8]): a slider for a size (lengths in centimetres), a
 *  segmented choice for an option, a switch for an on/off setting. A slider
 *  reports when it is let go, a choice and a switch at once; `onCommit` is
 *  given only the value that changed. The controls show `values` (the stored
 *  ones) again whenever those change. */
export function TunePanel(props: {
  params: ModelParam[];
  values: ModelValues;
  disabled: boolean;
  onCommit(values: ModelValues): void;
}): JSX.Element {
  const { params, values, disabled, onCommit } = props;
  const [draft, setDraft] = useState<ModelValues>(values);
  // By content: asking for the design again gives an equal object, and that
  // must not take a slider out of the user's hand.
  const stored = JSON.stringify(values);
  useEffect(() => setDraft(JSON.parse(stored) as ModelValues), [stored]);

  if (params.length === 0) {
    return (
      <p className="m-0 text-sm text-slate-700" data-testid="tune-panel">
        This design has nothing to tune.
      </p>
    );
  }

  const set = (name: string, value: ModelValues[string]) =>
    setDraft((now) => ({ ...now, [name]: value }));

  return (
    <section
      className="flex flex-col gap-4"
      data-testid="tune-panel"
      title={disabled ? WAIT_TITLE : undefined}
    >
      {params.map((param) => {
        const label = paramLabel(param);
        const value = draft[param.name] ?? param.default;
        if (param.kind === "param") {
          const k = param.unit === "m" ? 100 : 1;
          const step = param.step ?? (param.max - param.min) / 100;
          const shown = (typeof value === "number" ? value : param.default) * k;
          const back = (v: number) => (k === 100 ? metres(v) : v);
          return (
            <Slider
              key={param.name}
              label={label}
              min={param.min * k}
              max={param.max * k}
              step={step * k}
              unit={shownUnit(param.unit) || undefined}
              // The float's dust would make the range input round oddly.
              value={Math.round(shown * 1e6) / 1e6}
              disabled={disabled}
              onChange={(v) => set(param.name, back(v))}
              onCommit={(v) => {
                if (disabled) return;
                onCommit({ [param.name]: back(v) });
              }}
            />
          );
        }
        if (param.kind === "choice") {
          return (
            <div key={param.name} className="flex flex-col gap-1">
              <span className="text-sm font-semibold text-slate-800">
                {label}
              </span>
              <div
                role="radiogroup"
                aria-label={label}
                className="inline-flex flex-wrap gap-1 self-start rounded-lg bg-slate-100 p-1"
              >
                {param.options.map((option) => {
                  const checked = option === value;
                  return (
                    <button
                      key={option}
                      type="button"
                      role="radio"
                      aria-checked={checked}
                      disabled={disabled}
                      title={disabled ? WAIT_TITLE : undefined}
                      onClick={() => {
                        if (checked) return;
                        set(param.name, option);
                        onCommit({ [param.name]: option });
                      }}
                      className={[
                        "min-h-[44px] rounded-pill px-3 py-1 text-sm font-semibold transition-colors disabled:cursor-not-allowed disabled:opacity-50 md:min-h-0",
                        checked
                          ? "bg-white text-brand-dark shadow-card"
                          : "text-slate-600 hover:text-brand-dark",
                      ].join(" ")}
                    >
                      {humanise(option)}
                    </button>
                  );
                })}
              </div>
            </div>
          );
        }
        const on = value === true;
        return (
          <label
            key={param.name}
            className="flex min-h-[44px] items-center gap-2 text-sm text-slate-800 md:min-h-0"
          >
            <input
              type="checkbox"
              role="switch"
              aria-checked={on}
              checked={on}
              disabled={disabled}
              onChange={(event) => {
                set(param.name, event.target.checked);
                onCommit({ [param.name]: event.target.checked });
              }}
            />
            <span className="font-semibold">{label}</span>
          </label>
        );
      })}
    </section>
  );
}
